import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { FormContainer, SelectElement } from 'react-hook-form-mui';
import { useForm } from 'react-hook-form';
import {
  scheduleReportDialogSx,
  scheduleReportDialogTitleSx,
  scheduleReportChooseTextSx,
  scheduleReportDialogActionsSx,
  scheduleReportCancelButtonSx,
  scheduleReportNextButtonSx,
} from '../styles';
import { useGetReportsListQuery } from '../../requests/queries';
import { useUsers } from '../../../contexts/userContext';
import useYupValidationResolver from '../../../utils/formUtils/yupValidationResolver';
import { getSchemaResolver } from '../../../utils/reportingUtils/filterUtils';
import { MENU_PROPS } from '../../../scheduleReports/utils/helpers';

interface OverlayPageProps {
  open: boolean;
  onClose: () => void;
  onNext: (reportId: number | string) => void;
}

function OverlayPage({ open, onClose, onNext }: Readonly<OverlayPageProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useUsers();
  const isUserAuthenticated = user.fullyAuthenticated === 'authenticated';
  const { data: reportsListResponse, isLoading: isReportsLoading } = useGetReportsListQuery(isUserAuthenticated);
  const resolver = useYupValidationResolver(getSchemaResolver(['reports']));

  const formContext = useForm({
    resolver,
    defaultValues: {
      reports: '',
    },
  });
  const { watch, reset } = formContext;
  const watchedReports = watch('reports');

  const handleNext = () => {
    if (watchedReports) {
      onNext(watchedReports);
    }
  };
  const dropDownValues = useMemo(
    () =>
      reportsListResponse
        ? reportsListResponse?.map((el) => ({
            id: String(el?.id),
            label: el?.title,
            title: el?.title,
            type: el?.type,
          }))
        : [],
    [reportsListResponse]
  );

  const onScheduleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onScheduleClose} maxWidth='sm' fullWidth sx={scheduleReportDialogSx}>
      {/* Dialog Title with Close Button */}
      <DialogTitle sx={scheduleReportDialogTitleSx}>
        {t('admin.customReports.selectReportToSchedule')}
        <IconButton aria-label='close' onClick={onScheduleClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent>
        <Typography sx={scheduleReportChooseTextSx}>{t('admin.customReports.chooseReport')}</Typography>
        <FormContainer formContext={formContext}>
          <SelectElement
            label={t('admin.customReports.reportLabel')}
            required
            name='reports'
            options={dropDownValues}
            variant='standard'
            fullWidth
            disabled={isReportsLoading}
            SelectProps={MENU_PROPS}
          />
        </FormContainer>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={scheduleReportDialogActionsSx}>
        <Button onClick={onScheduleClose} sx={scheduleReportCancelButtonSx}>
          {t('admin.customReports.cancel')}
        </Button>
        <Button
          variant='contained'
          sx={scheduleReportNextButtonSx(theme)}
          disabled={!watchedReports}
          onClick={handleNext}
        >
          {t('admin.customReports.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OverlayPage;
